/**
 * AP Invoices Demo — supplier invoice-history comparison, rendered inline at the
 * bottom of the invoice detail (not a modal).
 *
 * THROWAWAY DEMO CODE. Always mounted (in demo mode) so it can register the
 * `ap_show_supplier_history` chat tool; the tool flips the `supplierHistory`
 * slice open, which reveals this section — a trend chart + per-invoice table for
 * the supplier's recent invoices — and scrolls it into view. Reads static
 * fixture history (MRO Supply Co.). Delete with src/demo/ap.
 */

import { useEffect, useRef } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartLineIcon, XIcon } from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isApDemoMode } from './demo-mode';
import { updateApDemoState } from './fixture-store';
import { useApSupplierHistory } from './provenance';
import { registerApDemoTool } from './agent/script';
import {
  recentSupplierInvoices,
  SUPPLIER_HISTORY_CURRENCY,
  SUPPLIER_HISTORY_NAME,
  type SupplierInvoiceHistoryItem,
} from './agent/supplier-history';

function money(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: SUPPLIER_HISTORY_CURRENCY,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

interface TrendPoint {
  label: string;
  total: number;
  isCurrent: boolean;
}

function TrendChart({ rows }: { rows: SupplierInvoiceHistoryItem[] }) {
  // Chronological (oldest → newest) so the line reads left-to-right.
  const data: TrendPoint[] = [...rows].reverse().map((r) => ({
    label: shortDate(r.invoiceDate),
    total: r.totalAmount,
    isCurrent: r.isCurrent,
  }));
  const current = data.find((p) => p.isCurrent);

  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 16, right: 24, bottom: 8, left: 8 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e2e8f0"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: '#64748b' }}
            tickLine={false}
            axisLine={{ stroke: '#cbd5e1' }}
          />
          <YAxis
            tickFormatter={(v: number) => money(v)}
            tick={{ fontSize: 12, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={64}
          />
          <Tooltip
            formatter={(v: number) => [money(v), 'Invoice total']}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
          />
          <Line
            type="monotone"
            dataKey="total"
            stroke="#0f766e"
            strokeWidth={2.5}
            dot={{ r: 3, fill: '#0f766e' }}
            activeDot={{ r: 5 }}
            isAnimationActive
          />
          {current && (
            <ReferenceDot
              x={current.label}
              y={current.total}
              r={6}
              fill="#dc2626"
              stroke="#fff"
              strokeWidth={2}
              isFront
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function HistoryTable({ rows }: { rows: SupplierInvoiceHistoryItem[] }) {
  // rows are most-recent first; delta compares each invoice to the one before
  // it chronologically (i.e. the next row down).
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase text-muted-foreground">
            <th className="py-2 pr-3 font-medium">
              <T>Invoice</T>
            </th>
            <th className="py-2 pr-3 font-medium">
              <T>Date</T>
            </th>
            <th className="py-2 pr-3 text-right font-medium">
              <T>Subtotal</T>
            </th>
            <th className="py-2 pr-3 text-right font-medium">
              <T>Tax</T>
            </th>
            <th className="py-2 pr-3 text-right font-medium">
              <T>Total</T>
            </th>
            <th className="py-2 text-right font-medium">
              <T context="change versus the previous invoice">Δ vs prior</T>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const prior = rows[i + 1];
            const deltaPct = prior
              ? ((r.totalAmount - prior.totalAmount) / prior.totalAmount) * 100
              : null;
            return (
              <tr
                key={r.id}
                className={cn(
                  'border-b last:border-0',
                  r.isCurrent && 'bg-emerald-50',
                )}
              >
                <td className="py-2 pr-3 font-medium">
                  <span className="flex items-center gap-2">
                    {r.invoiceNumber}
                    {r.isCurrent && (
                      <Badge className="bg-emerald-600 text-[10px] text-white">
                        <T context="badge on the invoice currently open">
                          This invoice
                        </T>
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                  {shortDate(r.invoiceDate)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {money(r.subtotal)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {money(r.taxAmount)}
                </td>
                <td className="py-2 pr-3 text-right font-medium tabular-nums">
                  {money(r.totalAmount)}
                </td>
                <td className="py-2 text-right tabular-nums">
                  {deltaPct === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={cn(
                        deltaPct > 0 ? 'text-red-600' : 'text-emerald-700',
                      )}
                    >
                      {deltaPct > 0 ? '+' : ''}
                      {deltaPct.toFixed(0)}%
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Section() {
  const history = useApSupplierHistory();
  const open = !!history?.open;
  const rows = recentSupplierInvoices();
  const ref = useRef<HTMLDivElement>(null);

  // Register the chat tool once; firing it opens this section.
  useEffect(() => {
    registerApDemoTool('ap_show_supplier_history', () => {
      updateApDemoState((s) => {
        s.supplierHistory = { open: true };
      });
    });
  }, []);

  // Scroll the section into view when it opens (after it has rendered).
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(
      () => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      60,
    );
    return () => clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const close = () =>
    updateApDemoState((s) => {
      s.supplierHistory = { open: false };
    });

  return (
    <Card ref={ref} className="mx-1 scroll-mt-2 border-emerald-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <ChartLineIcon className="size-4 text-emerald-600" />
            <T context="supplier invoice history section title">
              Recent invoices from <Var>{SUPPLIER_HISTORY_NAME}</Var>
            </T>
            <Badge
              variant="outline"
              className="border-emerald-300 bg-emerald-50 text-xs text-emerald-700"
            >
              <T>
                Last <Var>{rows.length}</Var>
              </T>
            </Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={close}
            aria-label="Dismiss"
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="supplier invoice history section subtitle">
            How this invoice compares to the supplier's recent run rate.
          </T>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <TrendChart rows={rows} />
        <HistoryTable rows={rows} />
      </CardContent>
    </Card>
  );
}

export function SupplierInvoiceHistorySection() {
  if (!isApDemoMode()) return null;
  return <Section />;
}
